import type {
  ClearGateResponse,
  GateCode,
  GateOutcome,
  GateReadinessResponse,
} from '@/domain/types'
import { useApiQuery, type QueryState } from '@/api/useApiQuery'
import { useApiAction, type UseApiActionResult } from '@/api/useApiAction'

/**
 * Gate readiness and gate clears.
 *
 * The readiness endpoint is the single source of truth for "can this move".
 * The gate panel renders exactly what it returns and computes nothing of
 * its own — a second opinion in the browser is how a board ends up
 * disagreeing with the API that will reject the click.
 */
export function useGateReadiness(
  initiativeId: number,
  refreshKey: number = 0,
): QueryState<GateReadinessResponse> {
  return useApiQuery<GateReadinessResponse>(`/api/initiatives/${initiativeId}/gates`, refreshKey)
}

export interface ClearGateBody {
  outcome: GateOutcome
  /** Required for anything other than Advance. */
  reason?: string
  /** Criterion keys the clearer is ticking. Every Attest criterion must appear. */
  attestations?: string[]
  overrides?: { criterionKey: string; note?: string }[]
  overrideReason?: string
  overrideApproverObjectId?: string
  closureReason?: string
  closureNote?: string
  blockedReasonCode?: string
  blockedNote?: string
}

export interface UseClearGateResult extends UseApiActionResult<ClearGateResponse> {
  clear: (
    initiativeId: number,
    gateCode: GateCode,
    body: ClearGateBody,
  ) => Promise<ClearGateResponse | null>
}

export function useClearGate(): UseClearGateResult {
  const action = useApiAction<ClearGateResponse>()
  return {
    ...action,
    clear: (initiativeId, gateCode, body) =>
      action.run(`/api/initiatives/${initiativeId}/gates/${gateCode}/clear`, 'POST', body),
  }
}
